<template>
  <div class="mfr">
    <!-- Header -->
    <div class="mfr-header">
      <div class="mfr-header__titles">
        <h2 class="mfr-title">Mahalla mablag'larining sarfi yuzasidan hisobot</h2>
        <p class="mfr-subtitle">
          Har bir yilning 1–4-choragi uchun bittadan hisobot fayli. Yangi fayl joylansa,
          avvalgi versiya foydalanuvchilarga ko'rinmaydi.
        </p>
      </div>
      <div class="mfr-header__actions">
        <el-button type="primary" @click="openCreate">
          <el-icon><Upload /></el-icon><span>Hisobot joylash</span>
        </el-button>
      </div>
    </div>

    <!-- Filter -->
    <div class="mfr-toolbar">
      <label class="mfr-toolbar__label" for="mfr-year-filter">Yil</label>
      <el-select id="mfr-year-filter" v-model="year" class="mfr-toolbar__year">
        <el-option v-for="y in years" :key="y" :value="y" :label="y" />
      </el-select>
      <el-icon v-if="refetching" class="mfr-toolbar__spinner is-loading" aria-label="Yangilanmoqda">
        <Loading />
      </el-icon>
      <span class="mfr-toolbar__count" v-if="!initialLoading && !error">
        Yuklangan: <b>{{ uploadedCount }}</b> / 4
      </span>
    </div>

    <!-- Initial skeleton -->
    <el-skeleton v-if="initialLoading" :rows="4" animated class="mfr-skeleton" />

    <!-- Error -->
    <el-alert v-else-if="error" type="error" show-icon :closable="false" class="mfr-error">
      <template #default>
        <div class="mfr-error__body">
          <span>{{ error }}</span>
          <el-button size="small" type="primary" @click="fetchReports">Qayta urinish</el-button>
        </div>
      </template>
    </el-alert>

    <template v-else>
      <!-- Desktop table -->
      <el-table :data="rows" border stripe class="mfr-table" row-key="quarter">
        <el-table-column prop="year" label="Yil" width="90" align="center" />
        <el-table-column label="Chorak" width="120" align="center">
          <template #default="scope">
            <el-tag type="info" effect="plain">{{ scope.row.quarter }}-chorak</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Fayl" min-width="240">
          <template #default="scope">
            <div v-if="scope.row.report" class="mfr-file">
              <el-icon class="mfr-file__icon"><Document /></el-icon>
              <el-tooltip
                :content="fileName(scope.row.report)"
                placement="top"
                :disabled="!scope.row.report.file_name"
              >
                <span class="mfr-file__name">{{ fileName(scope.row.report) }}</span>
              </el-tooltip>
              <el-tag v-if="isBroken(scope.row.report)" type="warning" size="small" effect="plain">
                Fayl metama'lumoti topilmadi
              </el-tag>
            </div>
            <span v-else class="mfr-muted">—</span>
          </template>
        </el-table-column>
        <el-table-column label="Hajm" width="120">
          <template #default="scope">
            <span>{{ scope.row.report ? formatSize(scope.row.report.file_size) : "—" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Yangilangan" width="170">
          <template #default="scope">
            <span>{{ scope.row.report ? formatDate(scope.row.report.updated_at) : "—" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Holat" width="130" align="center">
          <template #default="scope">
            <el-tag v-if="scope.row.report" type="success" effect="light">Yuklangan</el-tag>
            <el-tag v-else type="info" effect="plain">Yuklanmagan</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Amallar" width="290" align="center">
          <template #default="scope">
            <template v-if="scope.row.report">
              <el-button
                size="small"
                :loading="downloadingId === scope.row.report.id"
                @click="download(scope.row.report)"
              >
                Yuklab olish
              </el-button>
              <el-button size="small" type="primary" plain @click="openReplace(scope.row)">
                Almashtirish
              </el-button>
              <el-button
                size="small"
                type="danger"
                plain
                :disabled="isBroken(scope.row.report)"
                :loading="deletingId === scope.row.report.id"
                @click="confirmRemove(scope.row)"
              >
                Olib tashlash
              </el-button>
            </template>
            <el-button v-else size="small" type="primary" @click="openReplace(scope.row)">
              Fayl yuklash
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Mobile cards -->
      <div class="mfr-cards">
        <div v-for="row in rows" :key="row.quarter" class="mfr-card">
          <div class="mfr-card__head">
            <el-tag type="info" effect="plain">{{ row.quarter }}-chorak</el-tag>
            <el-tag v-if="row.report" type="success" effect="light">Yuklangan</el-tag>
            <el-tag v-else type="info" effect="plain">Yuklanmagan</el-tag>
          </div>
          <template v-if="row.report">
            <div class="mfr-card__name">{{ fileName(row.report) }}</div>
            <div class="mfr-card__meta">
              {{ formatSize(row.report.file_size) }} · {{ formatDate(row.report.updated_at) }}
            </div>
            <el-tag
              v-if="isBroken(row.report)"
              type="warning"
              size="small"
              effect="plain"
              class="mfr-card__warning"
            >
              Fayl metama'lumoti topilmadi
            </el-tag>
            <div class="mfr-card__actions">
              <el-button
                class="mfr-card__button"
                :loading="downloadingId === row.report.id"
                @click="download(row.report)"
              >
                Yuklab olish
              </el-button>
              <el-button
                class="mfr-card__button"
                type="primary"
                plain
                @click="openReplace(row)"
              >
                Almashtirish
              </el-button>
              <el-button
                class="mfr-card__button"
                type="danger"
                plain
                :disabled="isBroken(row.report)"
                :loading="deletingId === row.report.id"
                @click="confirmRemove(row)"
              >
                Olib tashlash
              </el-button>
            </div>
          </template>
          <template v-else>
            <div class="mfr-card__meta">Bu chorak uchun hisobot joylanmagan</div>
            <div class="mfr-card__actions">
              <el-button class="mfr-card__button" type="primary" @click="openReplace(row)">
                Fayl yuklash
              </el-button>
            </div>
          </template>
        </div>
      </div>
    </template>

    <mahalla-fund-report-upload-dialog
      v-model="dialogVisible"
      :default-year="dialogYear"
      :default-quarter="dialogQuarter"
      :lock-period="dialogLocked"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import { Upload, Document, Loading } from "@element-plus/icons-vue";
import { blobErrorMessage } from "@/utils/common";
import api, {
  QUARTERS,
  formatFileSize,
  filenameFromResponse,
  saveBlob,
} from "./mahallaFundReportsApi";
import MahallaFundReportUploadDialog from "./MahallaFundReportUploadDialog.vue";

export default {
  name: "MahallaFundReports",
  components: { Upload, Document, Loading, MahallaFundReportUploadDialog },
  data() {
    return {
      year: new Date().getFullYear(),
      reports: [],
      initialLoading: true,
      refetching: false,
      error: null,
      downloadingId: null,
      deletingId: null,
      dialogVisible: false,
      dialogYear: new Date().getFullYear(),
      dialogQuarter: 1,
      dialogLocked: false,
    };
  },
  computed: {
    years() {
      const current = new Date().getFullYear();
      const list = [];
      for (let y = current + 1; y >= 2020; y--) list.push(y);
      return list;
    },
    // API faqat yuklanganlarini beradi — tanlangan yil doim 4 qatorga
    // normalize qilinadi, shunda admin 4/4 holatni bir qarashda ko'radi.
    rows() {
      return QUARTERS.map((quarter) => ({
        year: this.year,
        quarter,
        report:
          this.reports.find((r) => r.year === this.year && r.quarter === quarter) || null,
      }));
    },
    uploadedCount() {
      return this.rows.filter((row) => row.report).length;
    },
  },
  watch: {
    year() {
      this.fetchReports();
    },
  },
  mounted() {
    this.$store.commit("updateHeaderTitle", "Mahalla mablag'lari sarfi");
    this.fetchReports();
  },
  methods: {
    formatSize(bytes) {
      return formatFileSize(bytes);
    },
    formatDate(value) {
      if (!value) return "—";
      return this.$moment(value).format("DD.MM.YYYY HH:mm");
    },
    // file_* metadata null bo'lishi mumkin (eski/buzilgan file registry).
    isBroken(report) {
      return !report.file_name || report.file_size === null || report.file_size === undefined;
    },
    fileName(report) {
      return report.file_name || `hisobot-${report.year}-q${report.quarter}`;
    },
    fetchReports() {
      // Filter refetchda eski content saqlanadi — page flash qilmasin.
      if (!this.initialLoading) this.refetching = true;
      this.error = null;
      return api
        .list(this.year)
        .then(({ data }) => {
          this.reports = Array.isArray(data) ? data : [];
        })
        .catch((error) => {
          const status = error?.response?.status;
          if (status === 403) {
            this.error = "Sizda ushbu sahifaga kirish huquqi mavjud emas";
          } else {
            this.error =
              error?.response?.data?.message ||
              "Ma'lumotlarni yuklashda xatolik yuz berdi";
          }
        })
        .finally(() => {
          this.initialLoading = false;
          this.refetching = false;
        });
    },
    openCreate() {
      this.dialogYear = this.year;
      this.dialogQuarter = this.rows.find((row) => !row.report)?.quarter || 1;
      this.dialogLocked = false;
      this.dialogVisible = true;
    },
    openReplace(row) {
      this.dialogYear = row.year;
      this.dialogQuarter = row.quarter;
      this.dialogLocked = true;
      this.dialogVisible = true;
    },
    onSaved() {
      // Optimistic yangilanish yo'q: server yangi `id` qaytaradi va parallel
      // almashtirishda oxirgi transaction source of truth.
      this.fetchReports();
    },
    download(report) {
      this.downloadingId = report.id;
      api
        .download(report.file_id)
        .then((response) => {
          const filename = filenameFromResponse(response.headers, report.file_name);
          saveBlob(response.data, filename);
        })
        .catch(async (error) => {
          const status = error?.response?.status;
          if (status === 404) {
            // Report rowni avtomatik o'chirmaymiz.
            ElMessage.error("Fayl topilmadi");
            return;
          }
          ElMessage.error(
            await blobErrorMessage(error?.response, "Faylni yuklab olishda xatolik")
          );
        })
        .finally(() => {
          this.downloadingId = null;
        });
    },
    confirmRemove(row) {
      ElMessageBox.confirm(
        `${row.year}-yil ${row.quarter}-chorak hisoboti olib tashlanadi. Fayl foydalanuvchilarga ko'rinmay qoladi.`,
        "Hisobotni olib tashlash",
        {
          type: "warning",
          confirmButtonText: "Olib tashlash",
          cancelButtonText: "Bekor qilish",
          confirmButtonClass: "el-button--danger",
        }
      )
        .then(() => this.remove(row.report))
        .catch(() => {
          // bekor qilindi
        });
    },
    remove(report) {
      this.deletingId = report.id;
      api
        .remove(report.id)
        .then(() => {
          ElMessage.success("Hisobot olib tashlandi");
          this.fetchReports();
        })
        .catch((error) => {
          const status = error?.response?.status;
          if (status === 404) {
            ElMessage.warning("Hisobot allaqachon olib tashlangan");
            this.fetchReports();
            return;
          }
          if (status === 403) {
            ElMessage.error("Sizda ushbu amal uchun huquq yo'q");
            return;
          }
          ElMessage.error(
            error?.response?.data?.message || "Olib tashlashda xatolik yuz berdi"
          );
        })
        .finally(() => {
          this.deletingId = null;
        });
    },
  },
};
</script>

<style scoped>
.mfr {
  padding: 4px 0;
}
.mfr-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.mfr-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #303133;
}
.mfr-subtitle {
  margin: 6px 0 0;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.5;
  color: #909399;
}
.mfr-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 18px 0 12px;
  flex-wrap: wrap;
}
.mfr-toolbar__label {
  font-size: 13px;
  color: #606266;
}
.mfr-toolbar__year {
  width: 130px;
}
.mfr-toolbar__spinner {
  color: #409eff;
}
.mfr-toolbar__count {
  margin-left: auto;
  font-size: 13px;
  color: #606266;
}
.mfr-skeleton {
  margin-top: 16px;
}
.mfr-error__body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.mfr-file {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.mfr-file__icon {
  color: #909399;
}
.mfr-file__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mfr-muted {
  color: #c0c4cc;
}
.mfr-cards {
  display: none;
}
.mfr-card {
  padding: 14px;
  margin-bottom: 12px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fff;
}
.mfr-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.mfr-card__name {
  margin-top: 10px;
  font-weight: 600;
  word-break: break-word;
}
.mfr-card__meta {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.mfr-card__warning {
  margin-top: 8px;
}
.mfr-card__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}
.mfr-card__button {
  width: 100%;
  min-height: 44px;
  margin-left: 0;
}

@media (max-width: 768px) {
  .mfr-table {
    display: none;
  }
  .mfr-cards {
    display: block;
  }
  .mfr-header__actions {
    width: 100%;
  }
  .mfr-header__actions .el-button {
    width: 100%;
    min-height: 44px;
  }
  .mfr-toolbar__year {
    width: 100%;
  }
}
</style>
